// The Farsi landing-page data, lifted verbatim from apps/frontend/src/pages/Fa.tsx
// on 21 Aug 2026 when that page moved onto this app. Kept as one module so the
// copy, the catalogue numbers and the helpers stay together and stay diffable
// against where they came from.
//
// The Maktabkhooneh catalogue is here on purpose and its presence is a decision
// with history: see the Farsi Plan in the vault. It is NOT an oversight.

import {
  TbBrandGithubFilled,
  TbBrandLinkedin,
  TbBrandTelegram,
  TbBrandYoutube,
  TbMail,
} from "react-icons/tb";

// The four hero tiles, served from assets.gazar.dev. This app's CSP allows
// https: image sources, so they need no server change to render here.
//
// These are 800px WebP derivatives of the same English `hero-home-*` art, not
// new tiles, so the two sites still look like one brand. The originals are
// 2400x1792 PNGs weighing 2.59, 2.78, 2.77 and 2.90 MB, 11.0 MB for the four,
// and they were being served to a grid that renders each tile at about 300px
// wide. Measured on assets.gazar.dev 29 Aug 2026. React 19 hoists a
// `<link rel="preload" as="image">` for every eagerly-loaded SSR `<img>`, so
// all 11.0 MB was fetched at high priority on first paint, including on phones,
// where HeroArtGrid is `hidden lg:grid` and never draws them at all. This
// audience is largely on Iranian mobile connections. Same art, same scenes,
// 149 KB for the four: a 98.6% cut with no visible change at display size.
//
// Do not point these back at the full-size PNGs, and do not reuse one as the
// social card: see OG_IMAGE below.
export const HERO_ART = [
  "https://assets.gazar.dev/hero-home-1-w800_76abd394.webp",
  "https://assets.gazar.dev/hero-home-2-w800_24c9996f.webp",
  "https://assets.gazar.dev/hero-home-3-w800_bfabe75c.webp",
  "https://assets.gazar.dev/hero-home-4-w800_a2b1f2cd.webp",
];

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
export function faNum(value: string | number) {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)]);
}

// Mirrors the highlight stats on the English /about page, so the two pages
// cannot drift into quoting different numbers for the same career.
export const stats = [
  { value: "15", label: "سال تجربه‌ی مهندسی نرم‌افزار" },
  { value: "120", label: "مهندسی که منتور شده" },
  { value: "40", label: "ارائه و کارگاه" },
  { value: "60", label: "محصول منتشرشده" },
];

export const topics = [
  {
    title: "طراحی سیستم",
    body: "چگونه سیستمی بسازیم که زیر بار ترافیک واقعی دوام بیاورد. معماری، مقیاس‌پذیری، و بررسی تصمیم‌هایی که هزینه‌شان را در آینده پرداخت خواهید کرد.",
  },
  {
    title: "مهندسی نرم‌افزار",
    body: "تایپ‌اسکریپت، ری‌اکت و نود-جی‌اس. رویکردهایی برای نوشتن کدهای خوانا، تست‌پذیر و پایدار که فراتر از صرفاً اجرا شدن، ارزش خلق می‌کنند.",
  },
  {
    title: "هوش مصنوعی و ایجنت‌ها",
    body: "توسعه‌ی محصولات کاربردی با استفاده از مدل‌های زبانی. مسیری از خلق ایده تا پیاده‌سازی سیستمی پایدار در محیط عملیاتی.",
  },
];

/**
 * The Telegram group, and why this link and not the invite link.
 *
 * `@persiajs` is a public supergroup, verified live 22 Aug 2026: t.me/persiajs
 * shows "288 members, 55 online" and resolves to `tg://resolve?domain=persiajs`.
 * The `t.me/joinchat/...` invite for the same group works identically today,
 * but an invite link is REVOCABLE. Revoke it once and every copy of it on this
 * site, in every article and in every old message dies silently, with no error
 * anywhere and no way to notice except a reader telling you. A public username
 * cannot be revoked without renaming the group.
 *
 * Tagged `?src=fa` for the same reason every other outbound link here is
 * tagged: Telegram is now a second destination competing with the newsletter
 * for the same visitor, and in November somebody has to be able to say which
 * one this surface actually fed.
 */
export const TELEGRAM_GROUP = "https://t.me/persiajs";

export const socialLinks = [
  { href: TELEGRAM_GROUP, icon: TbBrandTelegram, label: "Telegram" },
  { href: "https://www.linkedin.com/in/gazar", icon: TbBrandLinkedin, label: "LinkedIn" },
  { href: "https://www.youtube.com/@gazarbreakpoint", icon: TbBrandYoutube, label: "YouTube" },
  { href: "https://github.com/ehsangazar", icon: TbBrandGithubFilled, label: "GitHub" },
  { href: "mailto:me@gazar.dev", icon: TbMail, label: "Email" },
];

export const navLinks = [
  { href: "#topics", label: "موضوع‌ها" },
  { href: "#courses", label: "دوره‌ها" },
  { href: "#about", label: "درباره‌ی من" },
  { href: "#newsletter", label: "خبرنامه" },
];

// Persian separates thousands with ٬ and decimals with ٫. faNum() only maps the
// digits, so the marks are swapped here rather than typed into the data, which
// keeps the course numbers as plain JS numbers that can be updated in place.
export const faCount = (n: number) => faNum(n.toLocaleString("en-US")).replace(/,/g, "٬");
export const faRating = (n: number) => faNum(n.toFixed(1)).replace(".", "٫");

// The video catalogue, re-read off maktabkhooneh.org/teacher/ehsan-gazar on
// 22 Aug 2026 10:12 BST and ordered by enrolments so the strongest four sit
// first. React Native is unchanged at 911; every other course moved, so these
// go stale and are worth re-reading whenever this file is touched. Slugs are Persian on purpose:
// that is the canonical URL, and the browser percent-encodes it.
//
// Deliberately no prices. They are toman figures behind a rotating discount, so
// they would be wrong within the week, and they would put a foreign-currency
// price list on a domain whose payment rail has already been reviewed once.
export const COURSE_BASE = "https://maktabkhooneh.org/course/";

/**
 * The instructor referral code, and why it is still empty.
 *
 * Maktabkhooneh pays an instructor 30% of a direct sale and 97% when the buyer
 * arrives through the instructor's own referral link or discount code. That is
 * not an internal rumour: it is published on maktabkhooneh.org/teaching/ and in
 * their instructor knowledge base, both read 22 Aug 2026. The 30% is of the
 * amount paid AFTER discounts, not of list price.
 *
 * The link format is confirmed and is NOT the one anybody guessed. It is
 * `?v=1&affiliate_code=<6 chars>`, verified three ways on 22 Aug 2026: from
 * Maktabkhooneh's own instructor-panel screenshot, from 318 real archived links
 * in the Wayback index, and from a live test showing the API answer with
 * `set-cookie: affiliate_code=...; Max-Age=7776000`, a 90-day attribution
 * cookie. Do not confuse it with the older `?affiliate=<slug>`, which takes a
 * human name and only sets a 7-day cookie.
 *
 * The code is PER-INSTRUCTOR, not per-course, so one six-character string
 * switches all eight links below.
 *
 * FILLED IN 22 Aug 2026, read out of the instructor panel at
 * mirza.maktabkhooneh.org/courses/<id>/promote. That page hands out a short link
 * per course (mktb.me/87md, /wd6t, /4o1x, /cwel ...) and states the 97% outright:
 * «با هر بار خرید موفق دوره از طریق این لینک، سهم شما از فروش دوره به ۹۷ درصد
 * افزایش می‌یابد». Resolving four of those short links showed they all expand to
 * the SAME affiliate_code, `yaQzOz`, on four different courses, which confirms
 * the code is per-instructor and one string covers the whole catalogue.
 *
 * Worth sizing honestly rather than assuming: on 22 Aug 2026 roughly 89% of the
 * Maktabkhooneh payout was a watch-time subscription pool that referral links do
 * not touch, and this page's own traffic was approximately zero. The swap is
 * cheap and correct; it is not, today, worth much.
 */
export const COURSE_REFERRAL_CODE = "yaQzOz";

/**
 * Every Maktabkhooneh link on this surface is built here, and only here.
 *
 * `v=1` is carried because every real 2026 affiliate link in the archive has it
 * and nothing documents what it means. Copying a working link exactly is the
 * cheap option; reverse-engineering a parameter on someone else's payment
 * attribution is not.
 */
export function courseUrl(slug: string): string {
  const url = `${COURSE_BASE}${slug}/`;
  if (!COURSE_REFERRAL_CODE) return url;
  return `${url}?v=1&affiliate_code=${encodeURIComponent(COURSE_REFERRAL_CODE)}`;
}

/**
 * The share card image for every route.
 *
 * Reused from the existing hero art on assets.gazar.dev rather than generated:
 * the one channel this surface depends on is Telegram, and a Telegram, LinkedIn
 * or WhatsApp preview card with no image is the cost of not having one. Nothing
 * about it is Farsi, which is deliberate. It carries no text at all, so it says
 * nothing that a payment processor could read.
 */
// Deliberately NOT HERO_ART[0] any more, changed 29 Aug 2026. The tiles became
// 800px WebP for weight, and WebP is exactly the format whose share-card support
// is inconsistent across platforms. Telegram is this surface's main rail, so the
// card stays a PNG and stays drawn for the slot: 1200x630, centre-cropped from
// the same first tile, 147 KB against the 2.59 MB it used to serve.
export const OG_IMAGE = "https://assets.gazar.dev/og-fa-home_d11511dd.png";
export const courses = [
  {
    slug: "آموزش-جاوا-اسکریپت-mk1122",
    title: "آموزش جاوا اسکریپت (JavaScript)",
    students: 7405,
    rating: 3.9,
    blurb:
      "از پایه‌های زبان تا کلوژر، پرامیس و رفتار ناهمگام؛ همان مفاهیمی که در کد واقعی و در مصاحبه‌ها سراغتان می‌آید.",
  },
  {
    slug: "آموزش-مقدماتی-react-js-mk1127",
    title: "آموزش React JS مقدماتی",
    students: 5250,
    rating: 4.2,
    blurb:
      "شروع کار با ری‌اکت: کامپوننت، استیت و هوک‌ها، تا اولین رابط کاربری‌ای که بعداً هم بشود نگهداری‌اش کرد.",
  },
  {
    slug: "آموزش-رایگان-clean-code-mk1125",
    title: "آموزش Clean Code",
    students: 4154,
    rating: 3.7,
    blurb:
      "نوشتن کدی که شش ماه بعد هم خوانده شود: نام‌گذاری، توابع کوچک و مرزبندی درست میان بخش‌های سیستم.",
  },
  {
    slug: "آموزش-پیشرفته-ری-اکت-mk1128",
    title: "آموزش React JS پیشرفته",
    students: 3976,
    rating: 3.7,
    blurb:
      "الگوهای پیشرفته‌ی ری‌اکت، مدیریت وضعیت، بهینه‌سازی رندر و ساختاردهی به پروژه‌هایی که بزرگ می‌شوند.",
  },
  {
    slug: "آموزش-طراحی-وبسایت-front-end-mk1658",
    title: "آموزش طراحی وب‌سایت (Front-End)",
    students: 3787,
    rating: 4.2,
    blurb:
      "مسیر کامل فرانت‌اند از HTML و CSS تا جاوااسکریپت و ری‌اکت، برای کسی که از صفر شروع می‌کند.",
  },
  {
    slug: "آموزش-node-js-mk1313",
    title: "آموزش Node.js",
    students: 1695,
    rating: 3.8,
    blurb:
      "سمت سرور با نود: ساخت API، کار با دیتابیس، و رساندن یک سرویس به جایی که بشود به آن تکیه کرد.",
  },
  {
    slug: "وبینار-مسیر-شغلی-فرانت-اند-mk2835",
    title: "وبینار مسیر شغلی فرانت‌اند",
    students: 1671,
    rating: 4.5,
    free: true,
    blurb:
      "یک وبینار رایگان درباره‌ی مسیر شغلی فرانت‌اند: از کجا شروع کنیم و کدام مهارت‌ها واقعاً تعیین‌کننده‌اند.",
  },
  {
    slug: "آموزش-react-native-mk1419",
    title: "آموزش React Native",
    students: 911,
    rating: 3.1,
    blurb:
      "ساخت اپلیکیشن موبایل با همان دانش ری‌اکت، بدون یادگیری دوباره‌ی زبان و ابزار بومی هر پلتفرم.",
  },
];
